import {Badge, Box, Button, Stack, TableCell, TableRow, Tooltip, Typography, IconButton} from "@mui/material";
import {getConversationIcon, getChatIcon} from "../../../../utils/functions";
import moment from "moment";
import React, {useMemo, useState} from "react";
import {useSetAtom} from "jotai";
import {SelectedConversationAtom} from "../atoms";
import {TOKEN} from "../../../../context/SignInContext";
import {IconChecks, IconEye, IconEyeOff, IconMessage2} from "@tabler/icons-react";
import {green, grey} from "@mui/material/colors";
import CompactAudioPlayer from "./CompactAudioPlayer"
import {IconRobot, IconCopy, IconCheck, IconPlayerPlay} from "@tabler/icons-react";


export const ConversationTableRow = ({conversation, project}) => {
    const setSelectedConversation = useSetAtom(SelectedConversationAtom);


    const getAudioUrl = () => {
        const path = conversation?.audio; // assume something like "/usr/src/asterisk/recordings/397/4367.wav"
        if (!path) return null;
        const base = project?.configuration?.conversation_logger_api || "https://api.voicebot.openbrain.io";
        const url = new URL("/api/file/audio", base);
        url.searchParams.append("path", path);
        url.searchParams.append("jwt", localStorage.getItem(TOKEN));

        return url.toString();
    };

    const [copiedField, setCopiedField] = useState(null);

    const handleCopy = (text, field) => {
        navigator.clipboard.writeText(text).catch(() => {
        });
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 1500);
    };

    const contact = useMemo(() => {
        return project?.phonebook?.[conversation?.sender] || null;
    }, [project?.phonebook?.[conversation?.sender], conversation?.sender])
    const selectedTags = project?.settings?.tags?.filter(i => conversation?.tags?.includes(i?.id));
    const successTag = project?.settings?.tags?.find(tag => tag?.id === project?.configuration?.success_tag) || null;


    const type = project?.settings?.projectType || "VOICE_BOT"

    const audioUrl = getAudioUrl();

    return (
        // <Tooltip title={"Click to open conversation"}>
        <TableRow key={conversation?.id} hover id={`conversation-${conversation?.id}`}
                  sx={{
                      cursor: "pointer",
                      transition: "background-color 0.12s ease",
                      "& td": {borderBottom: `1px solid ${grey[100]}`},
                      "&:hover": {bgcolor: grey[50]},
                  }}
                  onClick={() => setSelectedConversation(conversation)}>
            <TableCell padding={"checkbox"} sx={{py: 1, px: 1, width: {xs: 84, md: 30}, maxWidth: {xs: 84, md: 30}}}>
                <Stack direction={"row"} gap={1} alignItems={"center"} sx={{minWidth: 0}}>
                    {type === "CHAT_BOT" ? getChatIcon(conversation?.status) :
                        conversation?.resolved && conversation?.status !== "SUCCESS" ?
                            <IconChecks size={18} color={grey[600]}/> :
                            getConversationIcon(conversation?.status)
                    }
                    {/*{type === "CHAT_BOT" && <IconRobot size={16} color={grey[600]}/>}*/}
                    <Typography fontSize={13} component={"span"} noWrap
                                sx={{overflow: "hidden", textOverflow: "ellipsis"}}>
                        {conversation?.id}
                    </Typography>
                </Stack>
            </TableCell>

            <TableCell padding={"checkbox"}
                       sx={{py: 1, px: 1, width: {xs: "auto", md: "220px"}, minWidth: {xs: 0, md: "220px"}}}>
                <Box sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    width: "100%",
                    '&:hover .copy-btn': {opacity: 1}
                }}>
                    <Typography sx={{cursor: "pointer"}}
                                fontSize={13} fontWeight={500} noWrap>
                        {String(contact || conversation?.sender || '').length > 20
                            ? `${String(contact || conversation?.sender).slice(0, 20)}...`
                            : (contact || conversation?.sender)}
                    </Typography>
                    {type === "CHAT_BOT" && (
                        <IconButton
                            size="small"
                            className="copy-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(contact || conversation?.sender, "contact");
                            }}
                            sx={{opacity: 0, transition: 'opacity 0.15s', p: 0.25, ml: 0.5, flexShrink: 0}}
                        >
                            {copiedField === "contact" ? <IconCheck size={13} color="green"/> : <IconCopy size={13}/>}
                        </IconButton>
                    )}
                </Box>
            </TableCell>
            <TableCell padding={"checkbox"}
                       sx={{py: 1, px: 1, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                <Typography noWrap
                            fontSize={13}>{moment(conversation?.dateCreated).format("DD MMM YYYY HH:mm")}</Typography>
            </TableCell>
            <TableCell
                padding={"checkbox"}
                sx={{
                    py: 1,
                    px: 1,
                    display: {
                        xs: "table-cell", md: "table-cell",
                        width: type === "CHAT_BOT" ? 30 : 60,
                        maxWidth: type === "CHAT_BOT" ? 30 : 60
                    },
                }}>
                <Typography noWrap fontSize={12.5}
                            sx={{textTransform: "capitalize", overflow: "hidden", textOverflow: "ellipsis"}}>
                    {conversation?.status?.toLowerCase()?.replaceAll("_", " ")}
                    {conversation?.vdn || conversation?.transferNumber ? ` (${conversation?.vdn || conversation?.transferNumber})` : ""}
                </Typography>
            </TableCell>

            <TableCell padding={"checkbox"} sx={{py: 1, px: 1, overflow: "hidden", width: "80px", maxWidth: "80px", display: {xs: "none", md: "table-cell"}}}>
                {(() => {
                    const displayText = conversation.status === "SUCCESS" && successTag?.title
                        ? successTag?.title
                        : selectedTags?.length > 0
                            ? selectedTags?.filter(i => !!i?.title)?.map(i => i?.title)?.join(", ")
                            : "Just started...";

                    return (
                        <Tooltip title={displayText}>
                            <Typography noWrap fontSize={11} fontFamily="Inter"
                                        sx={{overflow: "hidden", textOverflow: "ellipsis"}}>
                                {displayText}
                            </Typography>
                        </Tooltip>
                    );
                })()}
            </TableCell>
            <TableCell padding={"checkbox"}
                       sx={{py: 1, px: 0, width: 32, maxWidth: 32, display: {xs: "none", md: "table-cell"}}}>
                <Stack direction={"row"} gap={0.25} alignItems={"center"} justifyContent={"center"}>
                    <Badge invisible={(conversation?.comments?.length || 0) === 0} color={"error"} variant="dot">
                        <IconMessage2 size={14} color={grey[600]}/>
                    </Badge>
                    <Tooltip title={conversation?.monitoredBy || "Not supervised"}>
                        {conversation?.monitoredBy ? <IconEye color={grey[600]} size={14}/> :
                            <IconEyeOff color={grey[600]} size={14}/>}
                    </Tooltip>
                </Stack>
            </TableCell>

            {type === "VOICE_BOT" ? <TableCell padding={"checkbox"} sx={{width: "auto"}}>
                    <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"flex-end"}
                           sx={{width: "auto", py: 0.5, fontSize: 13}}>
                        <Box sx={{display: {xs: "none", md: "flex"}, fontSize: "inherit", pr: 1}}>
                            {audioUrl ? (
                                <CompactAudioPlayer src={audioUrl}/>
                            ) : (
                                <Stack direction="row" alignItems="center" gap={0.75} sx={{width: 110}}>
                                    <IconButton size="small" disabled sx={{p: 0.25, "&.Mui-disabled": {color: grey[500]}}}>
                                        <IconPlayerPlay size={16}/>
                                    </IconButton>
                                </Stack>
                            )}
                        </Box>
                    </Stack>
                </TableCell>
                : null}
        </TableRow>
        // </Tooltip>
    )
}